import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';

interface TimestampProps {
  date: string | number;
  'data-chromatic'?: string;
  format?: (date: moment.Moment) => string;
}

const fromNowDiff = (momentDate: moment.Moment) =>
  moment.duration(moment().valueOf() - momentDate.valueOf());

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const nextUpdateTimeout = (momentDate: moment.Moment) => {
  const diff = fromNowDiff(momentDate);

  // moment's `fromNow` rounds up to `an hour ago` for 45+ minutes
  if (Math.abs(diff.asMinutes()) <= 45) {
    return 60 * 1000;
  }

  if (Math.abs(diff.asHours()) <= 24) {
    return 30 * 60 * 1000;
  }

  return -1;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const fromNow = (momentDate: moment.Moment) => {
  const diff = fromNowDiff(momentDate).asSeconds();

  // For timestamps more recent than a minute moment ago will produce "a few
  // seconds ago" which is too long to fit in some parts of the UI.
  if (diff >= 0 && diff < 60) {
    return 'just now';
  }

  return momentDate.fromNow();
};

const Timestamp = ({ date, format = fromNow, ...rest }: TimestampProps) => {
  const momentDate = useMemo(() => moment(date), [date]);
  const [relativeTime, setRelativeTime] = useState(() => format(momentDate));

  const formatEvent = useEffectEvent(format);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const innerTick = () => {
      setRelativeTime(formatEvent(momentDate));
      const ms = nextUpdateTimeout(momentDate);
      timeout = ms > 0 ? setTimeout(innerTick, ms) : null;
    };

    innerTick();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [momentDate]);

  return (
    <time dateTime={momentDate.toISOString()} {...rest}>
      {relativeTime}
    </time>
  );
};

export default Timestamp;
